import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError, get } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const MODES = [
  { value: "all", label: "Export all matching records" },
  { value: "new_since_last", label: "Export new since last export" },
];

export function ExportDialog({ open, onClose, scopeType, scopeId, scopeName, filters }) {
  const [format, setFormat] = useState("csv");
  const [mode, setMode] = useState("all");
  const [preset, setPreset] = useState("standard_lead");
  const [columns, setColumns] = useState(null);

  const meta = useQuery({
    queryKey: ["export-columns"],
    queryFn: () => get("/exports/columns"),
    enabled: open,
  });
  const counts = useQuery({
    queryKey: ["export-preview", scopeType, scopeId],
    queryFn: () => get(`/exports/preview-count?scope_type=${scopeType}${scopeId ? `&scope_id=${scopeId}` : ""}`),
    enabled: open,
  });

  const activeColumns = useMemo(() => {
    if (columns) return columns;
    return meta.data?.presets?.[preset] || [];
  }, [columns, meta.data, preset]);

  const create = useMutation({
    mutationFn: () => api.post("/exports", {
      scope_type: scopeType, scope_id: scopeId, filters: filters || {},
      columns: activeColumns, format, mode,
    }),
    onSuccess: ({ data }) => {
      toast.success(`Export queued (${data.estimated_rows} rows) — you will be notified when ready`);
      onClose();
    },
    onError: (e) => toast.error(
      e.response?.data?.detail?.message || e.response?.data?.detail || apiError(e)),
  });

  const pii = (meta.data?.columns || []).filter((c) => c.pii && activeColumns.includes(c.key));
  const blocked = pii.length > 0 && meta.data?.can_export_pii === false;
  const rows = mode === "new_since_last"
    ? (counts.data?.new_since_last_export ?? counts.data?.total)
    : counts.data?.total;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl bg-white" data-testid="export-dialog">
        <DialogHeader><DialogTitle>Export — {scopeName}</DialogTitle></DialogHeader>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="space-y-1.5">
            <Label>Format</Label>
            <Select value={format} onValueChange={setFormat}>
              <SelectTrigger data-testid="export-format-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="csv">CSV (recommended for large volumes)</SelectItem>
                <SelectItem value="xlsx">XLSX</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Scope</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="export-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {MODES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Column preset</Label>
            <Select value={preset} onValueChange={(v) => { setPreset(v); setColumns(null); }}>
              <SelectTrigger data-testid="export-preset-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="phones_only">Only phone numbers</SelectItem>
                <SelectItem value="standard_lead">Standard lead fields</SelectItem>
                <SelectItem value="all_fields">All fields</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="max-h-48 overflow-auto rounded-md border border-slate-200 p-2" data-testid="export-columns">
          <div className="grid grid-cols-2 gap-1.5 md:grid-cols-3">
            {(meta.data?.columns || []).map((column) => (
              <label key={column.key} className="flex items-center gap-2 text-xs text-slate-700">
                <Checkbox
                  data-testid={`export-column-${column.key}`}
                  checked={activeColumns.includes(column.key)}
                  onCheckedChange={(checked) => {
                    const next = new Set(activeColumns);
                    if (checked) next.add(column.key); else next.delete(column.key);
                    setColumns([...next]);
                  }}
                />
                {column.label}{column.pii ? <span className="text-amber-600">•</span> : null}
              </label>
            ))}
          </div>
        </div>

        <p className="text-xs text-slate-500" data-testid="export-row-estimate">
          {rows != null ? `${rows.toLocaleString()} rows will be exported` : "Calculating…"}
          {counts.data?.last_export_at ? ` · last export ${new Date(counts.data.last_export_at).toLocaleString()}` : ""}
          {format === "xlsx" && (rows || 0) > 200000 ? " · CSV is recommended for this volume" : ""}
        </p>
        {blocked ? (
          <p className="text-xs text-rose-600" data-testid="export-pii-warning">
            Your role cannot export personal data ({pii.map((c) => c.label).join(", ")}). Remove
            those columns or ask a Super Admin for the export-PII permission.
          </p>
        ) : null}

        <DialogFooter>
          <Button
            className="bg-blue-600 hover:bg-blue-700"
            disabled={!activeColumns.length || blocked || create.isPending}
            onClick={() => create.mutate()}
            data-testid="export-submit"
          >
            {create.isPending ? "Queueing…" : "Start background export"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
